/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-05-22 15:26:55
 *
 *******************************************************/
// @flow
import _ from 'lodash';

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Select, SkeletonDisplayText, SkeletonBodyText } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import ActionChoosePackage from 'src/components/views/subscription-v4/package/action-choose-package';
import {
  LARGE_PRODUCT,
  MAX_PRODUCTS_FREEMIUM,
  PACKAGES_V4,
  TYPE_PACKAGES_V4,
  REQUEST_LARGE_PLAN,
  LARGE_PRODUCT_LABEL,
  PRICING_V4_START_WITH_PRODUCTS
} from 'src/constants/subscription';
import { connect } from 'react-redux';
import TickIcon from 'src/assets/images/subscription-v4/tick.svg';
import StarIcon from 'src/assets/images/subscription-v4/star.svg';
import { getCorrectPlanV4 } from 'src/helpers/subscription-v3-helper';
import { SUBSCRIPTION_V4_DOWNGRADE_COMPARE } from 'src/constants/subscription';

const checkDowngrade = (correctPlanName, packageName) => {
  if (correctPlanName.includes('professional') && packageName === 'essential')
    return {
      data: SUBSCRIPTION_V4_DOWNGRADE_COMPARE.PRO_2_ESS,
      title: 'AdminHTML.SubscriptionV4.Banner.DowngradeCompare.toEssential'
    };
  if (correctPlanName.includes('professional') && packageName === 'basic')
    return {
      data: SUBSCRIPTION_V4_DOWNGRADE_COMPARE.PRO_2_BASIC,
      title: 'AdminHTML.SubscriptionV4.Banner.DowngradeCompare.toBasic'
    };
  if (correctPlanName.includes('essential') && packageName === 'basic')
    return {
      data: SUBSCRIPTION_V4_DOWNGRADE_COMPARE.ESS_2_BASIC,
      title: 'AdminHTML.SubscriptionV4.Banner.DowngradeCompare.toBasic'
    };
  return false;
};

const getCurrentProducts = (tempPackageSelected, packageName = 'basic') => {
  if (!tempPackageSelected) {
    return PRICING_V4_START_WITH_PRODUCTS[packageName];
  }

  // eslint-disable-next-line
  if (tempPackageSelected === 500 && ['essential', 'professional'].includes(packageName)) {
    return PRICING_V4_START_WITH_PRODUCTS[packageName];
  }

  return tempPackageSelected;
};

const getTextTerm = (products, packageName, currentPackages = []) => {
  const validProducts = getCurrentProducts(products, packageName);
  // eslint-disable-next-line
  const plan = currentPackages.find(e => e.products == validProducts);
  return plan?.term ?? '';
};

//------- Package Card --------
const PackageCard = props => {
  const {
    i18n,
    packageInfo = {},
    invoice,
    confirmationUrl,
    currentTotalProducts,
    gettingInvoice,
    gettingPackage,
    packagePlans = [],
    allPackagePlans = {},
    packageSelected: tempPackageSelected,
    onChangeNumProducts,
    packageNameType,
    correctPlan,
    subscription,
    showOption,
    tenantApp
  } = props;
  const { selectedPlan = '' } = tenantApp;
  const packageName = _.get(props, 'packageInfo.name', '');

  const [currentPackageSelected, setCurrentPackageSelected] = useState(tempPackageSelected);
  useEffect(() => {
    if (!tempPackageSelected) {
      const correctPlan = packagePlans.find(element => element.products > currentTotalProducts);
      const packageSelected = correctPlan?.products || PRICING_V4_START_WITH_PRODUCTS[packageName];
      if (packageName !== 'basic' && packageSelected === '500') {
        setCurrentPackageSelected('5000');
        return;
      }

      setCurrentPackageSelected(packageSelected);
    } else {
      if (packageName !== 'basic' && tempPackageSelected === '500') {
        setCurrentPackageSelected('5000');
        return;
      }
      setCurrentPackageSelected(tempPackageSelected);
    }

    if (packageName === 'freemium') {
      setCurrentPackageSelected(currentTotalProducts);
    }
  }, [tempPackageSelected, packageName, packagePlans]);

  const featuresLabel = _.get(props, 'packageInfo.featuresLabel', '');
  const features = _.get(props, 'packageInfo.features', []);

  const currentPlanProducts = _.get(invoice, 'chargeInfo.products', 0);
  const correctProducts =
    currentTotalProducts > currentPlanProducts ? currentTotalProducts : currentPlanProducts;

  const listPlanInPackages = _.get(allPackagePlans, packageName, []);
  const currentPackages = _.clone(listPlanInPackages).filter(e => e.products <= LARGE_PRODUCT);

  currentPackages.push({
    name: REQUEST_LARGE_PLAN,
    products: LARGE_PRODUCT_LABEL,
    term: '50,000+',
    price: listPlanInPackages.find(e =>
      correctProducts > LARGE_PRODUCT ? e.products >= correctProducts : e.products > LARGE_PRODUCT
    )?.price
  });
  const correctPlanName = _.get(correctPlan, 'name', '');
  const isCurrentPlan = correctPlanName.includes(packageName);

  // Init list of product count
  const options = currentPackages.map(item => {
    const product = item.term.match(/(.*) [Pp]roducts/);
    const label = product ? product[1] : '';
    return {
      value: `${item.products}`,
      name: item.name,
      label: item.name === REQUEST_LARGE_PLAN ? item.term : label
    };
  });

  const [packageStateInfo, setPackageInfoState] = useState(packageInfo);
  const { title, name } = packageStateInfo;

  const isDisabled =
    (currentPackageSelected !== LARGE_PRODUCT_LABEL &&
      currentTotalProducts > parseInt(currentPackageSelected)) ||
    (currentTotalProducts > MAX_PRODUCTS_FREEMIUM && packageName === TYPE_PACKAGES_V4.Freemium);

  // Redirect to shopify confirmation page
  useEffect(() => {
    if (confirmationUrl) {
      window.top.location.href = confirmationUrl;
      return;
    }
  }, [confirmationUrl]);

  useEffect(() => {
    setPackageInfoState(packageInfo);
  }, [packageInfo]);

  // eslint-disable-next-line
  const correctPlanObj = currentPackages.find(e => e.products >= currentPackageSelected);

  const setUpsellPackage = useCallback(packageName => {
    const packageUpsell = PACKAGES_V4.find(_package => packageName === _package.name);
    setPackageInfoState(packageUpsell);
  }, []);

  const actionInfo = {
    correctPlan,
    correctPlanName,
    packageName,
    subscription
  };

  const term = getTextTerm(correctPlan?.products || [], packageName, currentPackages);

  return (
    <>
      <Card>
        {isCurrentPlan ? (
          <div className="tag tag-current-plan">
            {i18n.translate('AdminHTML.SubscriptionV4.currentPlan')}
          </div>
        ) : packageName === 'essential' ? (
          <div className="tag tag-most-popular">
            {i18n.translate('AdminHTML.SubscriptionV4.mostPopular')}
          </div>
        ) : (
          <div></div>
        )}
        <div className="package-name" style={{ display: 'flex', alignItems: 'center' }}>
          {i18n.translate(title)}{' '}
          {['essential', 'professional'].includes(packageName) && (
            <img style={{ marginLeft: 8 }} src={StarIcon} alt="star" />
          )}
        </div>
        <div className={packageName}></div>
        {gettingInvoice || gettingPackage ? (
          <SkeletonDisplayText size="small" />
        ) : (
          <div className="price">
            ${packageName === 'freemium' ? 0 : correctPlanObj?.price}
            <span className="permonth">
              {i18n.translate('AdminHTML.SubscriptionV4.Common.perMonth')}
            </span>
          </div>
        )}
        {gettingInvoice || gettingPackage ? (
          <SkeletonBodyText />
        ) : (
          <>
            {packageName === 'freemium' ? (
              <div className="select-product">
                {i18n.translate('AdminHTML.SubscriptionV4.Packages.freemium.upto')}
              </div>
            ) : selectedPlan === '' || showOption ? (
              <div className="select-product">
                <Select
                  options={options}
                  value={`${currentPackageSelected}`}
                  onChange={e => onChangeNumProducts(e)}
                />
                <p>{i18n.translate('AdminHTML.SubscriptionV4.Common.products')}</p>
              </div>
            ) : (
              <div className="select-product">{term}</div>
            )}
            <div className="package-action-v4">
              <ActionChoosePackage
                packageNameType={packageNameType}
                packageInfo={packageStateInfo}
                primary={packageName === TYPE_PACKAGES_V4.Essential}
                packagePlans={packagePlans}
                disabled={isDisabled}
                correctPlan={correctPlanObj}
                packageSelected={currentPackageSelected}
                allPackagePlans={allPackagePlans}
                setUpsellPackage={e => {
                  setUpsellPackage(e);
                }}
                products={currentPackageSelected}
                actionInfo={actionInfo}
                downgradeData={checkDowngrade(correctPlanName, packageName)}
                activeSelectPackage={props.activeSelectPackage}
              />
            </div>
          </>
        )}
        <div className="question-title">{i18n.translate(featuresLabel)}</div>
        <div className="advances-package">
          {features.map((e, index) => (
            <div key={`${name}-advanceName-${index}`} className="advance-line">
              <img src={TickIcon} alt="stars" width={15} height={15} />
              <span>{i18n.translate(e)}</span>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const packagePlans = _.get(state.subscriptionV3, 'packagePlans', {});
  const chargeInfo = _.get(state.subscriptionV3, 'invoice.chargeInfo', {});
  const selectedPlan = _.get(state.oauth.tenantApp, 'selectedPlan', '');
  const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts');
  // Get correct plan
  const correctPlan = getCorrectPlanV4(
    chargeInfo,
    packagePlans,
    currentTotalProducts,
    selectedPlan
  );
  return {
    tenantApp,
    correctPlan,
    subscription: _.get(state.oauth, 'subscription', {})
  };
};

export default withI18n()(connect(mapStateToProps, null)(PackageCard));
